// What's-New popup. Version-gated: fires once per app version, AFTER a new build is
// actually running (__APP_VERSION__ is baked into the bundle, so it only changes when the
// UpdatePrompt reload / redeploy has landed — the popup can never fire mid-update).
//
// DISMISSAL IS PER USER (v2 / PR-000056), held in users.whatsnew_seen_version. Phase 1 held
// it per DEVICE in localStorage `whatsnew_seen_version`, which is the reported bug: sign in
// on a second device and a note you had already read popped up again.
//
// ── The localStorage → server seed, and why it is here ──────────────────────────────────
// The first note was published on 2026-08-31, so by the time this ships the popup HAS
// fired: everyone who dismissed it holds a localStorage value with no server counterpart.
// Left alone they would be shown that note a SECOND time, on the very release whose point
// is that dismissal now follows you between devices. So when the server value is null and
// this device holds one, the local value is POSTed to seed the server BEFORE deciding
// whether to show anything. Small, one-time, and self-extinguishing (the column is only
// null once).
//
// What it does NOT fix, deliberately: a user who dismissed on device A and whose first
// post-upgrade load is on device B still sees it once, because device B's localStorage is
// empty and there is nothing to seed from. That is unavoidable and acceptable — it is one
// show, and the server carries the marker from then on.
//
// The localStorage write is KEPT on dismiss as a per-device fallback, so a dismissal that
// happens while the server write fails still suppresses the popup on this device and can
// seed the server later.
//
// Best-effort, ONLINE-ONLY, and never blocks the app: everything runs after mount, any
// failure (offline, 429, 5xx) renders nothing, and the seen version only advances on an
// explicit "Got it" — a skipped popup shows again next load. Role filtering is client-side
// relevance (audience_roles ∩ user.roles, or 'all'); the server already restricts the feed
// to PUBLISHED release notes.
//
// ── 0.12.5 Part K — walking a STACK of unread notes ────────────────────────────────────
// Several releases in quick succession means someone returning after a few days meets more
// than one unread note. That already worked (every note newer than the user's seen version
// is fetched and paged); what did not was that "Got it" sat on EVERY page while dismiss()
// marks the RUNNING BUILD seen, so page 1 of 4 dismissed all four. The primary button now
// reads "Next" until the last page. The decision is in lib/whatsNewPager.ts, gated by
// database/verify-whatsnew-pager.ts.
//
// ⚠ ORDER IS NEWEST FIRST AND STAYS THAT WAY. content.service.ts:311-320 returns notes
// `published_at desc` with a semver re-sort, so "Next" walks BACKWARDS in time. That reads
// oddly and is still right: anyone who abandons the stack part way has then read the MOST
// RECENT note, which is the one that matters. Reversing it would leave the abandoner having
// read only the oldest. Each card carries its own version badge, so the direction is visible
// rather than hidden. Ben's call if he wants it chronological; it is one .reverse() and it
// must not be changed without him saying so.

import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { fetchWhatsNew, fetchWhatsNewSeen, postWhatsNewSeen, type ContentItemOut } from '../lib/content';
import { semverGt } from '../lib/semver';
import { whatsNewPagerState } from '../lib/whatsNewPager';
import { useUpdates } from './content/UpdatesModal';
import { C, F, M } from './ops-board/theme';
import ReleaseNoteCard from './content/ReleaseNoteCard';

export const WHATSNEW_SEEN_KEY = 'whatsnew_seen_version';

// localStorage can throw (Safari private mode, storage disabled). It is a fallback here,
// never the source of truth, so a failure must not take the popup down with it.
function readLocalSeen(): string | null {
  try { return localStorage.getItem(WHATSNEW_SEEN_KEY); } catch { return null; }
}
function writeLocalSeen(v: string): void {
  try { localStorage.setItem(WHATSNEW_SEEN_KEY, v); } catch { /* fallback only */ }
}

export default function WhatsNewModal() {
  const { user } = useAuth();
  const { openUpdates } = useUpdates();
  const [items, setItems] = useState<ContentItemOut[]>([]);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!user) { setItems([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const state = await fetchWhatsNewSeen();
        if (cancelled || !state) return; // offline / failed → no popup this load
        const local = readLocalSeen();

        // Seed: the server has never recorded a dismissal for this user but this device
        // has one. Carry it over BEFORE the gate below, so a note this person already
        // dismissed is not shown to them a second time by the upgrade itself.
        let seen = state.seen_version;
        if (seen === null && local) {
          seen = (await postWhatsNewSeen(local)) ?? local;
          if (cancelled) return;
        }

        // Gate purely on the RUNNING bundle's version vs what this USER has seen.
        if (seen && !semverGt(__APP_VERSION__, seen)) return;

        const rows = await fetchWhatsNew(seen ?? undefined);
        if (cancelled || !rows) return;
        const mine = rows.filter((r) =>
          // Only notes for versions up to the RUNNING app — a note published ahead for
          // the NEXT release stays hidden until that build is actually live.
          (!r.version || !semverGt(r.version, __APP_VERSION__))
          && (r.audience_roles.includes('all') || r.audience_roles.some((role) => user.roles.includes(role))),
        );
        setItems(mine);
        setPage(0);
      } catch {
        // offline / any failure → no popup this load; the version gate stays put.
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (!user || items.length === 0) return null;
  const item = items[Math.min(page, items.length - 1)];
  // 0.12.5 Part K — "Next" through a stack, "Got it" only at the end. The decision lives in
  // lib/whatsNewPager.ts so database/verify-whatsnew-pager.ts can assert it without a DOM; the
  // three flags below MUST come from that one call rather than being recomputed inline, or the
  // footer promise and the button's behaviour can drift apart again.
  const { showPager, primaryLabel, primaryDismisses, showDismissNote } = whatsNewPagerState(items.length, page);

  const dismiss = () => {
    // Server first (it is the source of truth and follows the user between devices);
    // localStorage stays as the per-device fallback if that write does not land.
    void postWhatsNewSeen(__APP_VERSION__);
    writeLocalSeen(__APP_VERSION__);
    setItems([]);
  };

  const seePast = () => {
    // Reading the history is not the same act as acknowledging this version, so this does
    // NOT dismiss — the popup is simply closed and will show again next load if the user
    // never pressed "Got it".
    setItems([]);
    openUpdates();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,41,66,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 310, padding: 20 }}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, width: '100%', maxWidth: 460, maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(15,41,66,0.25)', fontFamily: F }}>
        <div style={{ padding: '16px 20px 12px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span style={{ fontFamily: M, fontSize: 12, color: '#fff', background: C.accent, padding: '2px 8px', borderRadius: 6, fontWeight: 700 }}>
              v{item.version ?? __APP_VERSION__}
            </span>
            <span style={{ fontSize: 15, fontWeight: 800, color: C.text }}>What's new</span>
          </span>
          {/* ⚠ THE CHEVRONS STAY. They are for browsing BACK over a stack you have already walked;
              the primary button drives the flow forward. Same visibility rule as before — a stack
              only — now read from whatsNewPagerState so there is one definition of "is a stack". */}
          {showPager && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} aria-label="Previous"
                style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, cursor: page === 0 ? 'default' : 'pointer', color: C.textDim, padding: '1px 7px', opacity: page === 0 ? 0.4 : 1 }}>‹</button>
              <span style={{ fontSize: 11, color: C.textMuted }}>{page + 1} of {items.length}</span>
              <button onClick={() => setPage((p) => Math.min(items.length - 1, p + 1))} disabled={page === items.length - 1} aria-label="Next"
                style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, cursor: page === items.length - 1 ? 'default' : 'pointer', color: C.textDim, padding: '1px 7px', opacity: page === items.length - 1 ? 0.4 : 1 }}>›</button>
            </span>
          )}
        </div>
        <div style={{ padding: '16px 20px 6px', overflowY: 'auto' }}>
          <ReleaseNoteCard data={item} assets={item.assets ?? []} />
        </div>
        <div style={{ padding: '10px 20px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <button
            onClick={seePast}
            style={{ background: 'none', border: 'none', padding: 0, fontSize: 12, color: C.accent, fontWeight: 700, cursor: 'pointer', fontFamily: F, textDecoration: 'underline' }}
          >
            See past updates
          </button>
          {/* ⚠ ONE BUTTON, TWO JOBS — deliberately not two buttons. The single-note case is the
              common path and must render exactly what it rendered before 0.12.5: same element,
              same styles, same "Got it", same dismiss(). whatsNewPagerState returns
              primaryDismisses = true whenever there is one note, so that path is untouched.

              ⚠ dismiss() ITSELF IS UNCHANGED. It still marks __APP_VERSION__ — the RUNNING build —
              seen, which is right: reaching the last page means the whole stack has been offered.
              What changed is only WHEN it can be reached. */}
          <button onClick={primaryDismisses ? dismiss : () => setPage((p) => Math.min(items.length - 1, p + 1))}
            style={{ border: 'none', borderRadius: 9, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', background: C.accent, color: '#fff', fontFamily: F }}>
            {primaryLabel}
          </button>
        </div>
        {/* ⚠ THE PROMISE ONLY WHERE IT IS KEPT. On a "Next" page this line described something the
            button would not do. It is tied to primaryDismisses, not written out separately. */}
        {showDismissNote && (
          <div style={{ padding: '0 20px 14px', fontSize: 11, color: C.textMuted }}>
            Won't show again on any of your devices
          </div>
        )}
      </div>
    </div>
  );
}
